import i18n from '@/i18n/config';
// Global imperative confirmation service.
// Any module can call `await confirm(title, description, { variant, confirmLabel, cancelLabel })`.
// Destruktive handlinger: `await confirmDestructive(...)` kan kreve en SKREVET bekreftelse
// (`typeToConfirm`) og/eller samle inn en valgfri slettegrunn (`reasonPresets`).
// The <ConfirmProvider /> registers the real handler at app root.
// Falls back to window.confirm if the provider isn't mounted yet.

export type ConfirmVariant = 'danger' | 'default';

export interface ReasonPreset { key: string; label: string }

export interface ConfirmOptions {
  variant?: ConfirmVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Brukeren må skrive nøyaktig denne teksten før bekreft-knappen aktiveres. */
  typeToConfirm?: string;
  /** Hurtigvalg for slettegrunn (+ fritekst). Grunnen returneres i resultatet. */
  reasonPresets?: ReasonPreset[];
  reasonLabel?: string;
  reasonRequired?: boolean;
}

export interface ConfirmResult { confirmed: boolean; reason: string }

export const DELETION_REASON_PRESETS = (): ReasonPreset[] => [
  { key: 'customer_cancelled', label: i18n.t('confirmService.deletionReasonPresets.kundeAvbestilte', 'Kunde avbestilte') },
  { key: 'duplicate', label: i18n.t('confirmService.deletionReasonPresets.dobbelRegistrering', 'Dobbel registrering') },
  { key: 'wrong_entry', label: i18n.t('confirmService.deletionReasonPresets.feilregistrert', 'Feilregistrert') },
  { key: 'other', label: i18n.t('confirmService.deletionReasonPresets.annet', 'Annet') },
];

type Handler = (title: string, description: string, options?: ConfirmOptions) => Promise<ConfirmResult>;

let handler: Handler | null = null;

export function registerConfirmHandler(fn: Handler | null) {
  handler = fn;
}

export function confirmDestructive(title: string, description: string = '', options: ConfirmOptions = {}): Promise<ConfirmResult> {
  if (handler) return handler(title, description, { variant: 'danger', ...options });
  // Fallback - should rarely happen; still better than silently returning false.
  const ok = window.confirm(description ? `${title}\n\n${description}` : title);
  return Promise.resolve({ confirmed: ok, reason: '' });
}

export async function confirm(title: string, description: string = '', options: ConfirmOptions = {}): Promise<boolean> {
  if (handler) return (await handler(title, description, options)).confirmed;
  return window.confirm(description ? `${title}\n\n${description}` : title);
}
